import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { translate } from '@jsverse/transloco';

import { Subject, takeUntil } from 'rxjs';

import { AppSharedModule } from 'app/app-shared.module';
import { Collection, CollectionService } from 'app/providers/storage-api/collection.service';
import { Field, FieldService } from 'app/providers/storage-api/field.service';
import { AlertService } from 'app/services/alert.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

interface FieldFormGroup {
  label: FormControl<string>;
  name: FormControl<string>;
  select: FormControl<boolean>;
}

type FieldFormArray = FormArray<FormGroup<FieldFormGroup>>;

interface Form {
  fields: FieldFormArray;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-calculated-field-config-modal',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './calculated-field-config-modal.component.html',
  styleUrls: ['./calculated-field-config-modal.component.scss'],
})
export class CalculatedFieldConfigModalComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() collection!: Collection;

  @Output() modalClosed = new EventEmitter<void>();

  private readonly _formBuilder = inject(FormBuilder);
  private readonly _fieldService = inject(FieldService);
  private readonly _collectionService = inject(CollectionService);
  private readonly _alertService = inject(AlertService);

  private readonly _destroy$ = new Subject<void>();

  isLoading = false;
  isUpdating = false;

  form: FormGroup<Form>;

  ngOnInit(): void {
    this.buildForm();

    this.loadFields();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  onOk(): void {
    this.modalClosed.emit();
  }

  get fields(): FieldFormArray {
    return this.form.controls.fields;
  }

  get hasFields(): boolean {
    return !!this.fields.length;
  }

  get selectedFields(): string[] {
    return this.fields
      .getRawValue()
      .filter((f) => f.select)
      .map((f) => f.name);
  }

  private buildForm(): void {
    this.form = this._formBuilder.group<Form>({
      fields: new FormArray([]),
    });
  }

  private async loadFields(): Promise<void> {
    this.isLoading = true;

    try {
      const fields = await this._fieldService.getFields();
      const calculatedFields = fields.items.filter((f) => f.fieldType === 'calculated');

      calculatedFields.forEach((f) => this.addFieldToForm(f));
    } catch (error) {
      this._alertService.showErrorToast(
        translate('calculated-field-config-modal.load-failed'),
        error
      );
    }

    this.isLoading = false;
  }

  private addFieldToForm(field: Field): void {
    const isSelected = this.collection.calculatedFields.includes(field.fieldName);

    const fieldFormGroup = this._formBuilder.group<FieldFormGroup>({
      label: new FormControl(field.label),
      name: new FormControl(field.fieldName),
      select: new FormControl(isSelected),
    });

    fieldFormGroup.controls.select.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this.handleFieldSelectChange());

    this.fields.push(fieldFormGroup);
  }

  private async handleFieldSelectChange(): Promise<void> {
    this.isUpdating = true;
    this.form.disable({ emitEvent: false });

    try {
      this.collection.calculatedFields = this.selectedFields;

      await this._collectionService.updateCollection(this.collection);
    } catch (error) {
      this._alertService.showErrorToast(
        translate('calculated-field-config-modal.update-failed'),
        error
      );
    }

    this.form.enable({ emitEvent: false });
    this.isUpdating = false;
  }
}
